import { useMemo } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { TransactionListItem } from '@/components/transaction/transaction-list-item'
import { ColorDot } from '@/components/ui/color-dot'
import { formatShortDate, formatAmount } from '@/lib/utils'
import type { Transaction } from '@/lib/hooks'

interface DailySpendingSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transactions: Transaction[]
  selectedDateStr: string | null
}

export function DailySpendingSheet({ open, onOpenChange, transactions, selectedDateStr }: DailySpendingSheetProps) {
  const selectedDayTotal = useMemo(() => {
    return transactions.reduce((sum, tx) => sum + tx.total_amount, 0)
  }, [transactions])

  const categoryBreakdown = useMemo(() => {
    if (transactions.length === 0) return []
    const map = new Map<string, { color: string | null; amount: number }>()
    for (const tx of transactions) {
      for (const item of tx.items) {
        const key = item.parent_category_name ?? item.category_name ?? '未分類'
        const existing = map.get(key)
        if (existing) {
          existing.amount += item.amount
        } else {
          map.set(key, { color: item.parent_category_color, amount: item.amount })
        }
      }
    }
    const total = selectedDayTotal
    return Array.from(map.entries())
      .map(([name, { color, amount }]) => ({
        name,
        color,
        amount,
        percentage: total > 0 ? Math.round((amount / total) * 100) : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
  }, [transactions, selectedDayTotal])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl min-h-[40vh] max-h-[70vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{selectedDateStr ? `${formatShortDate(selectedDateStr)}の支出` : ''}</SheetTitle>
          <SheetDescription>
            {formatAmount(selectedDayTotal)}・{transactions.length}件
          </SheetDescription>
        </SheetHeader>
        {categoryBreakdown.length > 0 && (
          <div className="space-y-1.5 px-4 pb-2">
            {/* Stacked category bar */}
            <div className="flex h-2 w-full gap-0.5 rounded-full bg-background">
              {categoryBreakdown.map((cat) => (
                <div
                  key={cat.name}
                  className="h-full first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${cat.percentage}%`,
                    backgroundColor: cat.color ?? undefined,
                    minWidth: cat.percentage > 0 ? '2px' : undefined,
                  }}
                />
              ))}
            </div>
            {/* Legend: dot + name + percentage */}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              {categoryBreakdown.map((cat) => (
                <span key={cat.name} className="flex items-center gap-1 text-xs">
                  <ColorDot color={cat.color} size="sm" />
                  <span>{cat.name}</span>
                  <span className="text-muted-foreground tabular-nums">{cat.percentage}%</span>
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-1 px-4 pb-8">
          {transactions.map((tx) => (
            <TransactionListItem
              key={tx.id}
              transaction={tx}
              to={`/transactions/${tx.id}?month=${tx.date.substring(0, 7)}`}
              showDate={false}
            />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  )
}
